import {type AiSearchAnswerStartedBy, logAiSearchInteraction} from 'analytics/aiSearchAnalytics';
import {
  type LiveAnswerPlacement,
  logLiveAnswerClosed,
  logLiveAnswerLinkClicked,
  logLiveAnswerShown,
} from 'analytics/liveAnswerAnalytics';
import {openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {pulseUserAtom} from 'atoms/auth';
import {growthbookCacheAtom} from 'atoms/growthbook';
import type {AiQueryTraceEntry} from 'components/ai/aiQueryConversation';
import {
  cancelSpeculativeRun,
  handOffSpeculativeRun,
  type SpeculativeProgress,
  type SpeculativeRun,
  startSpeculativeRun,
} from 'components/ai/speculativeAsk';
import {queryShape} from 'helpers/queryShape';
import {isSuperUser} from 'helpers/utils';
import {useAtomValue, useSetAtom} from 'jotai';
import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * How long something has to stay in the panel before its Ask turn starts, so
 * sweeping the mouse across the list doesn't start a turn per row.
 */
const DWELL_MS = 700;

/**
 * Answers already seen in this tab, by question, so going back to something
 * shows its answer at once instead of starting another turn. The real box
 * would cache server-side per user (and use stored summaries for people).
 */
const CACHE_MS = 30 * 60 * 1000;

/** How long a finished answer has to stay on screen to count as read. */
const READ_MS = 3000;

/** The one-line progress, plus everything streamed so far for the full answer. */
type LiveProgress = SpeculativeProgress & {
  answer: string;
  trace: AiQueryTraceEntry[];
  done: boolean;
};

// Only finished answers are kept, so a cached one is never cut off.
const answers = new Map<string, {progress: LiveProgress; at: number}>();

const cached = (question: string | null) => {
  const hit = question ? answers.get(question) : undefined;
  return hit && Date.now() - hit.at < CACHE_MS ? hit.progress : null;
};

/** One answer's time on screen, for its SHOWN, CLOSED and `answer_read` events. */
type AnswerView = {
  shownAt: number;
  cached: boolean;
  started: boolean;
  answeredAt?: number;
  linkClicks: number;
  continued: boolean;
  read: boolean;
  readTimer?: number;
};

const outcomeOf = (view: AnswerView) =>
  view.continued
    ? 'continued'
    : view.linkClicks > 0
      ? 'link'
      : view.answeredAt !== undefined
        ? 'read'
        : 'before_answer';

type ReadTrigger = 'visible' | 'link';

/**
 * A real Ask turn for whatever the panel shows, started once it has been there
 * a moment (search's speculative Ask, so one turn per tab: a new one cancels
 * the last). `progress` is the latest step and first sentence, plus the whole
 * answer and its steps so far;
 * `open` hands the turn to the Ask drawer so it carries on rather than
 * starting over. An answer finished in the last half hour comes from the cache.
 * `linkClicked` is for links in the answer. Each answer that started logs
 * SEARCH_LIVE_ANSWER_SHOWN once it's in and SEARCH_LIVE_ANSWER_CLOSED when it
 * leaves (continued in Ask, left by a link, just read, or closed early).
 * It counts as read once it has been on screen {@link READ_MS} after it
 * finished, or a link in it is clicked: one `answer_read` per answer, unless
 * it's carried on in Ask first (that's its `ask`; a read before then still
 * counts), and the server records the turn it didn't send. `startedBy` says
 * why the answer was there, as of when it was shown.
 */
export const useLiveAnswer = (
  question: string | null,
  placement: LiveAnswerPlacement = 'pane',
  startedBy: AiSearchAnswerStartedBy = 'typing'
) => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const {isSourcegraphEnabled} = useAtomValue(growthbookCacheAtom);
  const openAiSearchAndAsk = useSetAtom(openAiSearchAndAskAtom);
  const elevated = pulseUser ? isSuperUser(pulseUser) : false;
  const [progress, setProgress] = useState<LiveProgress | null>(null);
  const [started, setStarted] = useState(false);
  const runRef = useRef<SpeculativeRun | null>(null);
  const viewRef = useRef<AnswerView | null>(null);
  const readRef = useRef<((trigger: ReadTrigger) => void) | null>(null);

  useEffect(() => {
    const hit = cached(question);
    setProgress(hit);
    setStarted(Boolean(hit));
    if (!question) {
      viewRef.current = null;
      return undefined;
    }
    const view: AnswerView = {
      shownAt: performance.now(),
      cached: Boolean(hit),
      started: Boolean(hit),
      linkClicks: 0,
      continued: false,
      read: false,
    };
    viewRef.current = view;
    const markRead = (trigger: ReadTrigger) => {
      window.clearTimeout(view.readTimer);
      const run = runRef.current;
      if (view.read || view.continued || run?.handedOff) return;
      view.read = true;
      logAiSearchInteraction({
        action: 'answer_read',
        source: 'search_live_answer',
        placement,
        cached: view.cached,
        trigger,
        startedBy,
        queryShape: queryShape(question),
        answerMs: view.answeredAt === undefined ? undefined : view.answeredAt - view.shownAt,
        visibleMs: performance.now() - view.shownAt,
      });
      // A cached answer's turn was recorded, or not, the first time.
      run?.markSeen();
    };
    readRef.current = markRead;
    const answered = () => {
      if (view.answeredAt !== undefined) return;
      view.answeredAt = performance.now();
      logLiveAnswerShown({
        placement,
        cached: view.cached,
        answerMs: view.answeredAt - view.shownAt,
      });
      view.readTimer = window.setTimeout(() => markRead('visible'), READ_MS);
    };
    const close = () => {
      window.clearTimeout(view.readTimer);
      readRef.current = null;
      // A row swept past before its turn started isn't an answer anyone saw.
      if (!view.started) return;
      // Handed to the drawer some other way (a second click on its row, Enter).
      if (runRef.current?.handedOff) view.continued = true;
      const now = performance.now();
      logLiveAnswerClosed({
        placement,
        cached: view.cached,
        outcome: outcomeOf(view),
        visibleMs: now - view.shownAt,
        answerVisibleMs: view.answeredAt === undefined ? 0 : now - view.answeredAt,
        linkClicks: view.linkClicks,
      });
    };
    if (hit) {
      answered();
      return close;
    }
    const timer = window.setTimeout(() => {
      view.started = true;
      const run = startSpeculativeRun(question, {
        elevated,
        enableSourcegraph: isSourcegraphEnabled,
      });
      runRef.current = run;
      setStarted(true);
      run.onProgress((next) => {
        const live = {...next, ...run.snapshot};
        setProgress(live);
        if (live.done && live.answer) {
          answers.set(question, {progress: live, at: Date.now()});
          answered();
        }
      });
    }, DWELL_MS);
    return () => {
      window.clearTimeout(timer);
      close();
      const run = runRef.current;
      runRef.current = null;
      if (run) {
        run.onProgress(null);
        cancelSpeculativeRun(run);
      }
    };
    // `placement` is fixed for each place an answer shows, and `startedBy` is
    // as of when it was shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, elevated, isSourcegraphEnabled]);

  const linkClicked = useCallback(
    (href: string) => {
      const view = viewRef.current;
      if (!view) return;
      view.linkClicks += 1;
      logLiveAnswerLinkClicked({placement, href, answered: view.answeredAt !== undefined});
      readRef.current?.('link');
    },
    [placement]
  );

  const open = useCallback(() => {
    if (!question) return;
    if (viewRef.current) {
      viewRef.current.continued = true;
      window.clearTimeout(viewRef.current.readTimer);
    }
    handOffSpeculativeRun(question);
    openAiSearchAndAsk(question, 'search_live_answer');
  }, [question, openAiSearchAndAsk]);

  return {progress, started, open, linkClicked};
};
